import type { KeyModifiers, Tool, PointerSample, ToolContext } from "./Tool";

// Rotate the canvas view by dragging. Similar to Photoshop's Rotate View Tool (R).
// Drag horizontally to rotate; the rotation angle is stored and applied via
// setViewport. Reset with double-click or Escape key.

export interface ViewportWithRotation {
  readonly panX: number;
  readonly panY: number;
  readonly zoom: number;
  readonly rotation?: number; // degrees, clockwise
}

export class RotateViewTool implements Tool {
  readonly id = "rotate-view";
  readonly shortcut = "R";
  private dragStart: { sx: number; rotation: number } | null = null;

  onPointerDown(ctx: ToolContext, s: PointerSample): void {
    const vp = ctx.viewport as ViewportWithRotation;
    this.dragStart = {
      sx: s.x,
      rotation: vp.rotation ?? 0,
    };
  }

  onPointerMove(ctx: ToolContext, s: PointerSample): void {
    if (!this.dragStart) return;
    const dx = s.x - this.dragStart.sx;
    // 1 pixel of horizontal drag = 0.5 degrees of rotation
    const newRotation = this.dragStart.rotation + dx * 0.5;
    ctx.setViewport({ rotation: newRotation } as Partial<typeof ctx.viewport>);
  }

  onPointerUp(_ctx: ToolContext, _s: PointerSample): void {
    this.dragStart = null;
  }

  onKeyDown(ctx: ToolContext, key: string, modifiers?: KeyModifiers): void {
    if (key === "Escape" || key === "0") {
      // Reset rotation
      ctx.setViewport({ rotation: 0 } as Partial<typeof ctx.viewport>);
    }
  }

  /** Reset rotation to 0. */
  resetRotation(ctx: ToolContext): void {
    ctx.setViewport({ rotation: 0 } as Partial<typeof ctx.viewport>);
  }
}
